import clsx from "clsx";
import React, { RefObject } from "react";
import {
  PlayIcon,
  PauseIcon,
  MuteIcon,
  UnmuteIcon,
  FullScreen,
  ExitFullScreen,
} from "../icons";
import useVideoControls from "./useVideoControls";
import classes from "./video-controls.module.css";

interface VideoControlsProps {
  videoRef: RefObject<HTMLVideoElement>;
  containerRef?: RefObject<HTMLDivElement>;
  hasSound?: boolean;
  isActive?: boolean;
  size?: "sm" | "md" | "lg";
  color?: "primary" | "secondary";
  fullScreen?: boolean;
  componentName: string;
  onPlay?: () => void;
  onPause?: () => void;
  onMute?: () => void;
  onUnmute?: () => void;
}

const VideoControls = ({
  videoRef,
  containerRef,
  hasSound,
  isActive,
  size = "sm",
  color = "secondary",
  componentName,
  fullScreen,
  onPlay,
  onPause,
  onMute,
  onUnmute,
}: VideoControlsProps) => {
  const {
    isPlaying,
    isMuted,
    isFullScreen,
    onFullScreen,
    onMuteUnmute,
    onPlayPause,
  } = useVideoControls({
    videoRef,
    containerRef,
    onPlay,
    onPause,
    onMute,
    onUnmute,
  });

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    callback: (e: React.KeyboardEvent<HTMLButtonElement>) => void,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      callback(event);
    }
  };

  const sizeClass = {
    sm: classes.sizeSm,
    md: classes.sizeMd,
    lg: classes.sizeLg,
  }[size];

  const colorClass = {
    primary: classes.colorPrimary,
    secondary: classes.colorSecondary,
  }[color];

  const PlayPause = (
    <button
      className={clsx(
        !fullScreen && classes.playButton,
        classes.controlButton,
        sizeClass,
        colorClass,
      )}
      onClick={onPlayPause}
      onKeyDown={(e) => handleKeyDown(e, onPlayPause)}
      aria-label={isPlaying ? "Pause" : "Play"}
      data-testid={isPlaying ? "pause-button" : "play-button"}
      tabIndex={0}
    >
      {isPlaying ? (
        <PauseIcon label="pause icon" className={classes.icon} />
      ) : (
        <PlayIcon label="play icon" className={classes.icon} />
      )}
    </button>
  );

  const FullScreenButton = (
    <button
      className={clsx(classes.fullScreenButton, sizeClass, colorClass)}
      onClick={onFullScreen}
      onKeyDown={(e) => handleKeyDown(e, onFullScreen)}
      aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
      tabIndex={0}
    >
      {isFullScreen ? (
        <ExitFullScreen
          label="exit full screen icon"
          className={classes.icon}
        />
      ) : (
        <FullScreen label="full screen icon" className={classes.icon} />
      )}
    </button>
  );

  return (
    <>
      {hasSound && (
        <button
          className={clsx(
            classes.soundButton,
            classes.controlButton,
            sizeClass,
            colorClass,
          )}
          onClick={onMuteUnmute}
          onKeyDown={(e) => handleKeyDown(e, onMuteUnmute)}
          aria-label={isMuted ? "Unmute" : "Mute"}
          data-testid={isMuted ? "unmute-button" : "mute-button"}
          tabIndex={!componentName?.match(/carousel/i) ? 0 : isActive ? 0 : -1}
        >
          {isMuted ? (
            <UnmuteIcon label="unmute icon" className={classes.icon} />
          ) : (
            <MuteIcon label="mutes icon" className={classes.icon} />
          )}
        </button>
      )}
      {fullScreen ? (
        <div className={clsx(classes.iconContainer)}>
          {FullScreenButton}
          {PlayPause}
        </div>
      ) : (
        <>{PlayPause}</>
      )}
    </>
  );
};

export default VideoControls;
